import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchEntityMappings,
  executeTask,
  processEntityRecords,
  fetchDataverseEntities
} from "@/lib/api";
import { ProcessEntityRecordsParams
} from "@shared/api-types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { EntityFilterBuilder } from "@/components/filters/EntityFilterBuilder";

interface TaskButtonProps {
  name: string;
  description: string;
  icon: string;
  onExecute: (params?: any) => Promise<void>;
  isLoading: boolean;
  hasOptions?: boolean;
  taskName?: string;
}

const TaskButton = ({
  name,
  description,
  icon,
  onExecute,
  isLoading,
  hasOptions = false,
  taskName,
}: TaskButtonProps) => {
  const [recordLimit, setRecordLimit] = useState<number>(100);
  const [useEntityFilter, setUseEntityFilter] = useState<boolean>(false);
  const [useRecordLimit, setUseRecordLimit] = useState<boolean>(true);
  const [entityMapping, setEntityMapping] = useState<string>("");
  const [parentId, setParentId] = useState<string>("");
  const [excludeEmptyParentLookup, setExcludeEmptyParentLookup] = useState<boolean>(true);

  // Fetch entities from the entity-mappings endpoint
  const { data: entityMappings, isLoading: isLoadingEntities } = useQuery({
    queryKey: ["/api/entity-mappings"],
    queryFn: () => fetchEntityMappings(),
    staleTime: 60 * 1000, // 1 minutes
  });

  // Extract unique parent entities from the mappings
  const availableEntities = entityMappings
    ? entityMappings.map((m) => ({
        value: m.rowKey,
        label: `${m.parentEntity}_${m.childEntity}_${m.parentLookupField}_${m.childLookupField}`, // Compute name dynamically
      }))
    : [];

  // Fallback entities if no mappings are available
  const fallbackEntities = [
    { label: "System", value: "account" },
    { label: "Offline", value: "contact" }
   
  ];

  // Use available entities if we have them, otherwise use fallback
  const entityOptions =
    availableEntities.length > 0 ? availableEntities : fallbackEntities;

  const executeWithOptions = async () => {
    const params: Record<string, any> = {};

    // Only include record limit if the toggle is on
    if (useRecordLimit) {
      params.recordLimit = recordLimit;
    }

    if (entityMapping) {
      params.entityMapping = entityMapping;
    }

    // Only include parentId for single record processing
    if (useEntityFilter && parentId) {
      params.parentId = parentId;
    }

    // Add the new flag
    params.excludeEmptyParentLookup = excludeEmptyParentLookup;

    await onExecute(params);
  };

  return (
    <div className="mb-4 flex flex-col p-4 border rounded-md bg-card">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="material-icons text-primary text-2xl">{icon}</span>
        </div>
        <div className="flex-grow">
          <h3 className="text-lg font-semibold">{name}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>

        <Button
          variant="default"
          onClick={executeWithOptions}
          disabled={
            isLoading || !entityMapping || (useEntityFilter && !parentId)
          }
          className="md:w-auto w-full"
          title={
            !entityMapping
              ? "Please select an entity type"
              : useEntityFilter && !parentId
                ? "Please provide an entity ID"
                : ""
          }
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>Run Task</>
          )}
        </Button>
      </div>

      <div className="mt-4 pt-4 border-t">
        {/* First row - Entity Type Selection only */}
        <div className="mb-4">
          <div>
            <Label htmlFor="entityMapping" className="flex items-center gap-1">
              Entity Type <span className="text-red-500">*</span>
            </Label>
            <Select value={entityMapping} onValueChange={setEntityMapping}>
              <SelectTrigger
                id="entityMapping"
                className={`mt-1 ${!entityMapping && "border-red-300 focus:ring-red-500"}`}
              >
                <SelectValue placeholder="Select entity type" />
              </SelectTrigger>
              <SelectContent>
                {isLoadingEntities ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading entities...
                  </div>
                ) : entityOptions.length > 0 ? (
                  entityOptions.map((entity) => (
                    <SelectItem key={entity.value ?? ""} value={entity.value ?? ""}>
                      {entity.label}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-2 text-center text-sm text-muted-foreground">
                    No entities available
                  </div>
                )}
              </SelectContent>
            </Select>
            {!entityMapping && (
              <p className="text-xs text-red-500 mt-1">
                Required for all processing modes
              </p>
            )}
          </div>
        </div>

       
        {/* Third row - Entity Filter Settings */}
        <div className="mb-4 pt-4 border-t">
          <div className="flex flex-col space-y-2">
            <div className="flex flex-col space-y-2">
              <Label htmlFor="useEntityFilter" className="font-medium text-sm">
                Process a specific entity record
              </Label>
              <div className="flex items-center space-x-2">
                <Switch
                  id="useEntityFilter"
                  checked={useEntityFilter}
                  onCheckedChange={setUseEntityFilter}
                />
                <span className="text-sm text-muted-foreground">
                  {useEntityFilter
                    ? "On - Process single record"
                    : "Off - Process batch"}
                </span>
              </div>
            </div>

            {useEntityFilter && (
              <div className="mt-2">
                <Label htmlFor="parentId" className="flex items-center gap-1">
                  Entity ID (GUID) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="parentId"
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  className={`mt-1 font-mono ${!parentId && "border-red-300 focus:ring-red-500"}`}
                />
                {!parentId && (
                  <p className="text-xs text-red-500 mt-1">
                    Required when processing a specific entity
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
 {/* Second row - Record Limit Toggle and Selection */}
        <div className="mb-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                id="useRecordLimit"
                checked={useRecordLimit}
                onCheckedChange={setUseRecordLimit}
              />
              <Label
                htmlFor="useRecordLimit"
                className="font-medium text-sm cursor-pointer"
              >
                {useRecordLimit ? "Use record limit" : "No record limit"}
              </Label>
            </div>

            {useRecordLimit && (
              <div className="flex-grow max-w-xs ml-4">
                <Select
                  value={recordLimit.toString()}
                  onValueChange={(value) => setRecordLimit(parseInt(value))}
                >
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Select record limit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 records</SelectItem>
                    <SelectItem value="50">50 records</SelectItem>
                    <SelectItem value="100">100 records (default)</SelectItem>
                    <SelectItem value="500">250 records</SelectItem>
                    <SelectItem value="1000">1,000 records</SelectItem>
                    <SelectItem value="2500">2,500 records</SelectItem>
                    <SelectItem value="5000">5,000 records</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Fourth row - Exclude Empty Parent Lookup Toggle */}
        <div className="mb-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <Switch
              id="excludeEmptyParentLookup"
              checked={excludeEmptyParentLookup}
              onCheckedChange={setExcludeEmptyParentLookup}
            />
            <Label htmlFor="excludeEmptyParentLookup" className="font-medium text-sm cursor-pointer">
              Exclude records if <span className="font-mono">ParentLookupField</span> is empty/null
            </Label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function TasksPage() {
  const { toast } = useToast();
  const [currentTask, setCurrentTask] = useState<string | null>(null);

  // Fetch Dataverse entities for EntityFilterBuilder
  const {
    data: dataverseEntities,
    isLoading: isLoadingEntities,
    error: entitiesError,
  } = useQuery({
    queryKey: ["dataverse-entities"],
    queryFn: () => fetchDataverseEntities(),
    staleTime: 60 * 1000,
  });

  // Setup mutation for running tasks
  const executeTaskMutation = useMutation({
    mutationFn: (params: { taskName: string; taskParams?: any }) =>
      executeTask(params.taskName, params.taskParams),
    onSuccess: (data, params) => {
      toast({
        title: "Task Initiated",
        description:
          data.message || `${params.taskName} has been started successfully.`,
      });
      setCurrentTask(null);
    },
    onError: (error: Error, params) => {
      toast({
        title: "Task Failed",
        description: `Failed to start ${params.taskName}. ${error.message}`,
        variant: "destructive",
      });
      setCurrentTask(null);
    },
  });

  // Setup mutation for batch processing entity records
  const processRecordsMutation = useMutation({
    mutationFn: (params: ProcessEntityRecordsParams) =>
      processEntityRecords(params),
    onSuccess: (data) => {
      toast({
        title: "Batch Processing Started",
        description: `Batch ID: ${data.batchId}. Status: ${data.status}`,
      });
      setCurrentTask(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Batch Processing Failed",
        description: error.message,
        variant: "destructive",
      });
      setCurrentTask(null);
    },
  });

  const runTask = async (taskName: string) => {
    setCurrentTask(taskName);
    executeTaskMutation.mutate({ taskName });
  };

  const runTaskWithParams = async (taskName: string, taskParams?: any) => {
    setCurrentTask(taskName);
    executeTaskMutation.mutate({ taskName, taskParams });
  };

  const processBatch = async (params: ProcessEntityRecordsParams) => {
    setCurrentTask("process_entity_records");
    processRecordsMutation.mutate(params);
  };

  const isTaskRunning = (taskName: string) =>
    (executeTaskMutation.isPending || processRecordsMutation.isPending) &&
    currentTask === taskName;

  return (
    <div className="container py-6 px-6">
      <h1 className="text-3xl font-bold mb-6">Management Tasks</h1>

<Card>
        <CardHeader>
          <CardTitle>Field Mapping Tasks</CardTitle>
          <CardDescription>
            Tasks that synchronize data between fields in an entity. These operations may
            take some time to complete.
          </CardDescription>
        </CardHeader>
        <CardContent>
         <EntityFilterBuilder
          entities={dataverseEntities ?? []}
          isLoadingEntities={isLoadingEntities}
          onChange={() => {}}
        />
        {entitiesError && (
          <div className="text-red-500 text-sm mt-2">
            Failed to load entities: {entitiesError.message}
          </div>
        )}
        </CardContent>
      </Card>
  


      <Card>
        <CardHeader>
          <CardTitle>Data Synchronization Tasks</CardTitle>
          <CardDescription>
            Tasks that synchronize data between entities. These operations may
            take some time to complete.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TaskButton
            name="Batch Entity Processing"
            description="Process entity records in batches with configurable parameters"
            icon="batch_prediction"
            onExecute={processBatch}
            isLoading={isTaskRunning("process_entity_records")}
            hasOptions={true}
            taskName="process_entity_records"
          />
        </CardContent>
      </Card>
    </div>
  );
}
